import React, { useState } from 'react';
import { analyzeDream, DreamAnalysisResult } from '../services/geminiService';
import { MoonIcon, ChevronRightIcon, SparklesIcon, PhoneIcon, StarIcon, RefreshCwIcon } from './Icons';
import { useClient } from '../contexts/ClientContext';

// Archétypes et thèmes oniriques fréquents pour inspiration
const FREQUENT_DREAM_THEMES = [
    { title: "Voler dans les airs", desc: "Élévation, liberté ou besoin d'échapper à une contrainte terrestre." },
    { title: "Chute vertigineuse", desc: "Perte de contrôle, lâcher-prise émotionnel ou insécurité." },
    { title: "Eau profonde ou vague", desc: "Submersion par les émotions, intuition ou purification psychique." },
    { title: "Serpent ou animal sacré", desc: "Mue personnelle, guérison, tentation ou énergie de métamorphose." },
    { title: "Maison ou pièce secrète", desc: "Exploration des recoins inexplorés de votre psyché et talents enfouis." },
    { title: "Perte de dents", desc: "Angoisse du temps qui passe, transition majeure ou perte de repères." },
    { title: "Être poursuivi dans l'ombre", desc: "Part d'ombre non acceptée ou problème refoulé demandant affrontement." },
    { title: "Retard ou train manqué", desc: "Peur d'une opportunité manquée ou transition de vie imminente." }
];

const DREAM_MOODS = [
    { id: 'mystique', label: 'Mystique & Révélateur', color: 'border-amber-400/60 text-amber-300 bg-amber-500/10' },
    { id: 'etrange', label: 'Étrange & Bizarre', color: 'border-purple-400/60 text-purple-300 bg-purple-500/10' },
    { id: 'apaisant', label: 'Doux & Lumineux', color: 'border-cyan-400/60 text-cyan-300 bg-cyan-500/10' },
    { id: 'angoisse', label: 'Angoissant / Cauchemar', color: 'border-rose-400/60 text-rose-300 bg-rose-500/10' }
];

export const DreamInterpretation: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    const [dreamText, setDreamText] = useState('');
    const [selectedMood, setSelectedMood] = useState('mystique');
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0);
    const [result, setResult] = useState<DreamAnalysisResult | null>(null);

    const loadingSteps = [
        "Immersion dans les brumes de l'inconscient...",
        "Détection des symboles oniriques et archétypes...",
        "Harmonisation des synchronicités selon Jung & l'ésotérisme...",
        "Révélation du message secret de votre nuit..."
    ];

    const handleApplyTheme = (themeTitle: string) => {
        if (!dreamText.trim()) {
            setDreamText(`J'ai rêvé de ${themeTitle.toLowerCase()} : `);
        } else if (!dreamText.toLowerCase().includes(themeTitle.toLowerCase())) {
            setDreamText(prev => `${prev.trim()} (Élément onirique : ${themeTitle}) `);
        }
    };

    const handleAnalyze = async (textToUse?: string) => {
        const text = textToUse || dreamText;
        if (!text.trim()) return;
        setLoading(true);
        setResult(null);
        setLoadingStep(0);

        const timer = setInterval(() => {
            setLoadingStep(prev => (prev < loadingSteps.length - 1 ? prev + 1 : prev));
        }, 750);

        try {
            const res = await analyzeDream(text);
            setResult(res);

            if (client && res) {
                recordDivinationActivity(
                    'dreams', 
                    'Explication de Rêve', 
                    `Rêve : "${res.title || 'Songe nocturne'}"`, 
                    { dreamText: text, result: res, mood: selectedMood }
                );
            }
        } catch (e) {
            console.warn(e);
        } finally {
            clearInterval(timer);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setResult(null);
        setDreamText('');
    };

    const dreamLength = dreamText.trim().length;
    const dreamClarity = dreamLength === 0 
        ? null 
        : dreamLength < 40 
            ? { label: 'Bref', color: 'text-amber-300' }
            : dreamLength < 140 
                ? { label: 'Équilibré', color: 'text-cyan-300' }
                : { label: 'Idéal & Profond', color: 'text-emerald-400' };

    return (
        <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-4 max-w-4xl mx-auto animate-fade-in relative">
            
            {/* LUEUR MYSTIQUE D'ARRIÈRE-PLAN */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-80 bg-gradient-to-b from-indigo-600/15 via-purple-600/10 to-transparent blur-[90px] pointer-events-none"></div>

            {/* EN-TÊTE CÉLESTE */}
            <div className="text-center mb-8 relative z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-purple-950/80 via-indigo-900/60 to-purple-950/80 border border-indigo-500/40 shadow-[0_0_20px_rgba(99,102,241,0.25)] mb-3">
                    <SparklesIcon className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-200">
                        Clés Oniriques & Inconscient
                    </span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-indigo-200 tracking-tight">
                    Explication de Rêve
                </h1>
                
                <p className="text-xs sm:text-sm text-purple-200/80 max-w-xl mx-auto mt-2.5 leading-relaxed font-light">
                    Décryptez les songes secrets de votre nuit. Explorez les symboles, archétypes et révélations cachés sous le voile de votre inconscient.
                </p>
            </div>

            {/* HERO VISUEL ONIRIQUE VIVANT : LA PORTE DES SONGES */}
            {!result && !loading && (
                <div className="relative w-full rounded-3xl overflow-hidden mb-8 border border-purple-800/40 bg-gradient-to-b from-[#151036]/90 via-[#0F0A26]/90 to-[#08081A] shadow-[0_0_35px_rgba(79,70,229,0.25)] p-6 sm:p-8 backdrop-blur-xl transition-all group hover:border-indigo-500/50">
                    
                    {/* Étoiles et poussière cosmique animée */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-600/20 via-purple-900/10 to-transparent pointer-events-none"></div>
                    
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                        
                        {/* Lune Mystique avec Halo et Étoiles */}
                        <div className="relative flex items-center justify-center shrink-0">
                            {/* Halo pulsant */}
                            <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-tr from-amber-400/20 via-purple-500/20 to-indigo-500/30 blur-2xl animate-pulse-slow"></div>
                            
                            {/* Disque lunaire stylisé */}
                            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#1C1445] via-[#120D30] to-black border-2 border-amber-400/60 shadow-[0_0_25px_rgba(245,158,11,0.4)] flex items-center justify-center relative animate-float">
                                <MoonIcon className="w-12 h-12 sm:w-14 sm:h-14 text-amber-200 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" />
                                
                                <span className="absolute -top-1 -right-1 text-amber-300 animate-pulse">
                                    <SparklesIcon className="w-4 h-4 text-amber-300" />
                                </span>
                                <span className="absolute bottom-1 -left-1 text-purple-300 animate-twinkle">
                                    <StarIcon className="w-3.5 h-3.5 text-purple-300" />
                                </span>
                            </div>
                        </div>

                        {/* Texte d'immersion onirique */}
                        <div className="text-center md:text-left flex-1">
                            <div className="flex items-center justify-center md:justify-start gap-2 mb-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                                <span className="text-[11px] font-black uppercase tracking-wider text-emerald-400">
                                    Oracle Onirique Actif
                                </span>
                            </div>
                            <h2 className="text-lg sm:text-xl font-serif font-bold text-white mb-2">
                                Que vous murmure votre esprit pendant le sommeil ?
                            </h2>
                            <p className="text-xs text-purple-200/80 leading-relaxed max-w-md">
                                Chaque songe est un télégramme de l'âme. Décrivez les visages, animaux, lieux ou émotions ressentis pour en dévoiler le sens profond.
                            </p>
                        </div>

                        {/* Climat émotionnel rapide */}
                        <div className="shrink-0 flex flex-col items-center md:items-end gap-1.5 w-full md:w-auto">
                            <span className="text-[10px] uppercase font-bold text-purple-300/70 tracking-wider">
                                Tonalité de votre rêve :
                            </span>
                            <div className="grid grid-cols-2 gap-1.5 w-full md:w-auto">
                                {DREAM_MOODS.map(m => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => setSelectedMood(m.id)}
                                        className={`px-3 py-1.5 rounded-xl text-[11px] font-semibold border transition-all cursor-pointer ${
                                            selectedMood === m.id 
                                                ? `${m.color} shadow-[0_0_15px_rgba(168,85,247,0.3)] scale-102`
                                                : 'border-white/10 text-gray-400 hover:text-white bg-black/40'
                                        }`}
                                    >
                                        {m.label.split(' ')[0]}
                                    </button>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* FORMULAIRE DE SAISIE PRINCIPALE */}
            {!result && !loading && (
                <div className="space-y-6">
                    
                    {/* BOÎTE DE RÉDACTION AVEC CADRE LUMINEUX */}
                    <div className="bg-gradient-to-b from-[#130E2E]/95 via-[#0E0924]/95 to-[#08081A]/95 border-2 border-purple-800/50 hover:border-purple-600/80 focus-within:border-indigo-400/80 rounded-3xl p-5 sm:p-7 shadow-[0_0_40px_rgba(99,102,241,0.15)] transition-all space-y-4 backdrop-blur-xl relative overflow-hidden">
                        
                        <div className="flex items-center justify-between border-b border-purple-900/50 pb-3">
                            <label className="text-xs font-bold uppercase tracking-wider text-purple-200 flex items-center gap-2">
                                <MoonIcon className="w-4 h-4 text-amber-300" />
                                <span>Décrivez votre rêve en détail</span>
                            </label>
                            
                            {dreamClarity && (
                                <span className={`text-[11px] font-mono font-bold ${dreamClarity.color} bg-black/40 px-2.5 py-0.5 rounded-full border border-white/10`}>
                                    Récit {dreamClarity.label} ({dreamLength} car.)
                                </span>
                            )}
                        </div>

                        <textarea
                            rows={6}
                            value={dreamText}
                            onChange={(e) => setDreamText(e.target.value)}
                            placeholder="Exemple : J'étais au bord d'une immense mer violette la nuit, quand un aigle d'or m'a remis une clé scintillante. Je ressentais une intense paix mêlée de vertige..."
                            className="w-full bg-[#1A1138]/70 border border-purple-900/60 focus:border-indigo-400 rounded-2xl p-4 text-xs sm:text-sm text-white placeholder-purple-400/40 focus:outline-none transition-all resize-none shadow-inner leading-relaxed"
                        />

                        {/* BOUTONS D'ACTION DU FORMULAIRE */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                            {dreamText ? (
                                <button
                                    type="button"
                                    onClick={() => setDreamText('')}
                                    className="text-[11px] text-purple-400 hover:text-rose-300 transition-colors cursor-pointer self-start sm:self-center"
                                >
                                    Effacer le texte
                                </button>
                            ) : <div />}

                            <button
                                onClick={() => handleAnalyze()}
                                disabled={loading || !dreamText.trim()}
                                className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-amber-500 via-purple-600 to-indigo-600 hover:from-amber-400 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-widest shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] transition-all hover:scale-105 active:scale-95 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed border border-amber-300/40 flex items-center justify-center gap-2.5"
                            >
                                <SparklesIcon className="w-4 h-4 text-amber-200 animate-pulse" />
                                <span>Décoder Mon Rêve</span>
                                <ChevronRightIcon className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* SUGGESTIONS RAPIDES : THÈMES ONIRIQUES FRÉQUENTS */}
                    <div className="bg-[#100A26]/80 border border-purple-900/40 rounded-3xl p-5 shadow-xl space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
                                <SparklesIcon className="w-3.5 h-3.5 text-indigo-400" />
                                <span>Thèmes & Archétypes Fréquents (Cliquez pour ajouter)</span>
                            </span>
                            <span className="text-[10px] text-purple-400/60 hidden sm:inline">Inspiration rapide</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                            {FREQUENT_DREAM_THEMES.map((item, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleApplyTheme(item.title)}
                                    className="p-3 rounded-2xl bg-[#170E36]/70 hover:bg-purple-950/80 border border-purple-900/50 hover:border-indigo-400/60 text-left transition-all hover:-translate-y-0.5 group cursor-pointer"
                                >
                                    <div className="flex items-center justify-between text-xs font-bold text-white group-hover:text-amber-300 transition-colors">
                                        <span>{item.title}</span>
                                        <ChevronRightIcon className="w-3 h-3 text-purple-400 group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                    <p className="text-[10px] text-purple-300/70 mt-1 line-clamp-2 leading-relaxed font-light">
                                        {item.desc}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>

                </div>
            )}

            {/* ÉCRAN D'ANIMATION DE CHARGEMENT HYPNOTIQUE */}
            {loading && (
                <div className="max-w-lg mx-auto bg-gradient-to-b from-[#180E38] to-[#0D0721] border-2 border-indigo-500/50 rounded-3xl p-8 sm:p-12 shadow-[0_0_50px_rgba(99,102,241,0.3)] text-center space-y-6 animate-fade-in relative overflow-hidden">
                    
                    {/* Mandala lunaire tournoyant */}
                    <div className="relative w-28 h-28 mx-auto flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full border-4 border-dashed border-indigo-500/30 animate-spin-slow"></div>
                        <div className="absolute inset-2 rounded-full border-2 border-amber-400/40 border-t-amber-300 animate-spin" style={{ animationDuration: '2s' }}></div>
                        <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-purple-600/30 to-indigo-600/30 blur-md"></div>
                        <MoonIcon className="w-10 h-10 text-amber-300 animate-pulse relative z-10" />
                    </div>

                    <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 block mb-1">
                            Analyse Onirique Supérieure
                        </span>
                        <h3 className="text-xl font-serif font-bold text-white">
                            Exploration des Mondes Invisibles
                        </h3>
                        <p className="text-xs text-purple-200/90 font-mono mt-3 min-h-[2.5rem] flex items-center justify-center px-4">
                            {loadingSteps[loadingStep]}
                        </p>
                    </div>

                    {/* Barre de Progression Céleste */}
                    <div className="w-full bg-black/60 rounded-full h-2.5 overflow-hidden border border-indigo-500/40 p-0.5">
                        <div 
                            className="bg-gradient-to-r from-amber-400 via-purple-500 to-indigo-400 h-full rounded-full transition-all duration-700 shadow-[0_0_15px_rgba(245,158,11,0.8)]"
                            style={{ width: `${((loadingStep + 1) / loadingSteps.length) * 100}%` }}
                        ></div>
                    </div>

                    <span className="text-[10px] text-purple-400/70 block">
                        Interrogation de la mémoire des archétypes...
                    </span>
                </div>
            )}

            {/* RÉSULTAT COMPLET DE L'INTERPRÉTATION DU RÊVE */}
            {result && !loading && (
                <div className="space-y-6 animate-fade-in-up">
                    
                    {/* BANDEAU TITRE DU SONGE */}
                    <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#1E113E] via-[#140C2E] to-[#1E113E] border-2 border-amber-400/60 shadow-[0_0_40px_rgba(245,158,11,0.25)] relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none"></div>
                        
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-black uppercase tracking-widest mb-3">
                            <SparklesIcon className="w-3 h-3 text-amber-400" />
                            Révélation du Songe
                        </span>
                        
                        <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white tracking-tight">
                            {result.title}
                        </h2>
                        
                        <p className="text-xs text-purple-200/80 mt-2 max-w-lg mx-auto">
                            Interprétation symbolique et psychologique formulée selon les correspondances archétypales.
                        </p>
                    </div>

                    {/* DÉCRYPTAGE PSYCHOLOGIQUE & SPIRITUEL */}
                    <div className="p-6 sm:p-8 rounded-3xl bg-[#120D2C]/95 border border-purple-900/60 shadow-2xl space-y-4">
                        <div className="flex items-center gap-2 border-b border-purple-900/50 pb-3">
                            <MoonIcon className="w-5 h-5 text-indigo-400" />
                            <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                                Analyse & Signification Profonde
                            </h3>
                        </div>

                        <p className="text-xs sm:text-sm text-purple-100/90 leading-relaxed font-light whitespace-pre-line">
                            {result.analysis}
                        </p>
                    </div>

                    {/* SYMBOLES & ARCHÉTYPES IDENTIFIÉS */}
                    {result.symbols && result.symbols.length > 0 && (
                        <div className="p-6 sm:p-8 rounded-3xl bg-[#120D2C]/90 border border-purple-900/60 shadow-2xl space-y-4">
                            <div className="flex items-center gap-2 border-b border-purple-900/50 pb-3">
                                <SparklesIcon className="w-5 h-5 text-amber-400" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                                    Symboles & Archétypes Détectés
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                                {result.symbols.map((sym, idx) => (
                                    <div 
                                        key={idx} 
                                        className="p-4 rounded-2xl bg-gradient-to-br from-[#1A123D]/90 to-[#100B26]/90 border border-purple-800/60 hover:border-amber-400/60 transition-all space-y-1.5 shadow-md"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                                            <span className="text-xs font-bold text-amber-300 uppercase tracking-wide">
                                                {sym.name}
                                            </span>
                                        </div>
                                        <p className="text-xs text-purple-200/90 leading-relaxed pl-4">
                                            {sym.meaning}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CONSEIL ET GUIDANCE DU SONGE */}
                    {result.advice && (
                        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-purple-950/90 via-[#180E38] to-indigo-950/90 border-2 border-indigo-500/50 shadow-[0_0_30px_rgba(99,102,241,0.2)] text-center space-y-3 relative overflow-hidden">
                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300 block">
                                Message de Guidance Personnelle
                            </span>
                            <p className="text-sm sm:text-base font-serif italic text-white leading-relaxed max-w-2xl mx-auto">
                                "{result.advice}"
                            </p>
                        </div>
                    )}

                    {/* ACTIONS APRÈS INTERPRÉTATION */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <button
                            type="button"
                            onClick={handleReset}
                            className="w-full sm:w-auto px-7 py-4 rounded-full bg-[#150F33] hover:bg-[#1C1542] text-purple-200 text-xs font-bold border border-purple-800/60 transition-all cursor-pointer flex items-center justify-center gap-2"
                        >
                            <RefreshCwIcon className="w-3.5 h-3.5" />
                            <span>Interpréter un autre rêve</span>
                        </button>

                        <a
                            href="tel:0985405274"
                            className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2.5 transition-all hover:scale-105"
                        >
                            <PhoneIcon className="w-4 h-4 text-emerald-100" />
                            <span>Approfondir au 09 85 40 52 74</span>
                        </a>
                    </div>

                </div>
            )}

        </div>
    );
};

export default DreamInterpretation;
